import { useEffect, useState } from "react"
import { Link, router } from "@inertiajs/react"
import {
  Badge,
  Box,
  Button,
  Card,
  Flex,
  Heading,
  Link as RadixLink,
  Switch,
  Table,
  Text,
} from "@radix-ui/themes"
import { z } from "zod"
import { ProductSchema } from "@/schemas"

const PromptRankingSchema = z.object({
  id: z.number(),
  title: z.string(),
  latest_position: z.number().int().nullish(),
  ranked_at: z.string().nullish(),
})

const PropsSchema = z.object({
  project: z.object({
    id: z.number(),
    name: z.string(),
  }),
  product: ProductSchema,
  prompt_rankings: z.array(PromptRankingSchema),
})

type Props = z.infer<typeof PropsSchema>

function positionColor(position: number): "green" | "amber" | "gray" {
  if (position <= 3) return "green"
  if (position <= 10) return "amber"
  return "gray"
}

export default function ProductShow(rawProps: Props) {
  const props = PropsSchema.parse(rawProps)
  const [isTracked, setIsTracked] = useState(props.product.is_tracked)
  const [isUpdatingTracking, setIsUpdatingTracking] = useState(false)

  useEffect(() => {
    setIsTracked(props.product.is_tracked)
  }, [props.product.is_tracked])

  const handleTrackingChange = (checked: boolean) => {
    setIsTracked(checked)
    setIsUpdatingTracking(true)

    router.patch(
      `/projects/${props.project.id}/products/${props.product.id}`,
      { is_tracked: checked },
      {
        preserveScroll: true,
        preserveState: true,
        onError: () => setIsTracked((previous) => !previous),
        onFinish: () => setIsUpdatingTracking(false),
      },
    )
  }

  return (
    <Box width="100%">
      <Flex justify="between" align="start" mb="5" gap="4" wrap="wrap">
        <Box>
          <Text as="p" size="2" style={{ color: "var(--gray-11)" }}>
            {props.project.name}
          </Text>
          <Heading size="8">{props.product.name}</Heading>
        </Box>

        <Button variant="soft" color="gray" asChild>
          <Link href={`/projects/${props.project.id}`}>Back to project</Link>
        </Button>
      </Flex>

      <Card size="3" mb="4">
        <Flex direction="column" gap="4">
          <Flex justify="between" align="center" gap="4" wrap="wrap">
            <Box>
              <Heading size="5" mb="1">
                Tracking
              </Heading>
              <Text as="p" size="2" style={{ color: "var(--gray-11)" }}>
                Enable tracking to include this product in the project matrix.
              </Text>
            </Box>

            <Flex align="center" gap="3">
              <Switch checked={isTracked} disabled={isUpdatingTracking} onCheckedChange={handleTrackingChange} />
              <Text size="2" weight="medium">
                {isTracked ? "Tracked" : "Not tracked"}
              </Text>
            </Flex>
          </Flex>

          {props.product.website ? (
            <Text as="p" size="2" style={{ color: "var(--gray-11)" }}>
              Website:{" "}
              <RadixLink href={props.product.website} target="_blank" rel="noreferrer">
                {props.product.website}
              </RadixLink>
            </Text>
          ) : null}
        </Flex>
      </Card>

      <Card size="3">
        <Flex direction="column" gap="3">
          <Heading size="5">Latest position by prompt</Heading>
          <Text as="p" size="2" style={{ color: "var(--gray-11)" }}>
            Each row shows this product&apos;s latest observed position for that prompt.
          </Text>

          {props.prompt_rankings.length === 0 ? (
            <Text as="p" size="2" style={{ color: "var(--gray-11)" }}>
              No prompts in this project yet.
            </Text>
          ) : (
            <Box style={{ overflowX: "auto" }}>
              <Table.Root variant="surface" size="2">
                <Table.Header>
                  <Table.Row>
                    <Table.ColumnHeaderCell>Prompt</Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell justify="center">Latest position</Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell>Last update</Table.ColumnHeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {props.prompt_rankings.map((promptRanking) => (
                    <Table.Row key={promptRanking.id}>
                      <Table.RowHeaderCell>
                        <Link href={`/projects/${props.project.id}/prompts/${promptRanking.id}`}>
                          {promptRanking.title}
                        </Link>
                      </Table.RowHeaderCell>
                      <Table.Cell align="center">
                        {promptRanking.latest_position ? (
                          <Badge variant="soft" color={positionColor(promptRanking.latest_position)}>
                            #{promptRanking.latest_position}
                          </Badge>
                        ) : (
                          <Text color="gray">-</Text>
                        )}
                      </Table.Cell>
                      <Table.Cell>
                        {promptRanking.ranked_at ? (
                          <Text size="2">{new Date(promptRanking.ranked_at).toLocaleString()}</Text>
                        ) : (
                          <Text color="gray">-</Text>
                        )}
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table.Root>
            </Box>
          )}
        </Flex>
      </Card>
    </Box>
  )
}
